import type { InterfacePaginatedResponse } from '@interfaces/InterfacePaginatedResponse.ts';
import axiosClientCached from '@lib/axiosClientCached.ts';
import { logger } from '@lib/logging-system';

/**
 * Configuration for fetching paginated data
 */
export interface PaginationConfig {
  /** Number of items per page (default: 100) */
  perPage?: number;
  /** Starting page (default: 1) */
  startPage?: number;
  /** Maximum pages to fetch (default: unlimited) */
  maxPages?: number;
  /** Delay between page fetches in ms (default: 0) */
  delayBetweenPages?: number;
  /** Log progress (default: true) */
  logProgress?: boolean;
}

/**
 * Fetch a single page of paginated data
 */
export async function fetchPaginatedPage<T>(
  url: string,
  page: number,
  perPage: number = 100,
): Promise<InterfacePaginatedResponse<T>> {
  const separator = url.includes('?') ? '&' : '?';
  const paginatedUrl = `${url}${separator}page=${page}&per_page=${perPage}`;

  const response = await axiosClientCached.get<InterfacePaginatedResponse<T>>(paginatedUrl);
  return response.data;
}

/**
 * Fetch all pages of paginated data
 */
export async function fetchAllPaginatedPages<T>(url: string, config: PaginationConfig = {}): Promise<T[]> {
  const { perPage = 100, startPage = 1, maxPages, delayBetweenPages = 0, logProgress = true } = config;

  const allItems: T[] = [];
  let currentPage = startPage;
  let hasMorePages = true;
  let totalPages = 0;

  if (logProgress) {
    logger.debug('[fetchAllPaginatedPages] Starting pagination fetch:', { url, config });
  }

  while (hasMorePages) {
    try {
      const paginatedData = await fetchPaginatedPage<T>(url, currentPage, perPage);

      if (logProgress) {
        logger.debug(`[fetchAllPaginatedPages] Fetched page ${currentPage}:`, {
          currentPage: paginatedData.meta.current_page,
          lastPage: paginatedData.meta.last_page,
          total: paginatedData.meta.total,
          fetchedCount: paginatedData.data.length,
        });
      }

      allItems.push(...paginatedData.data);
      totalPages = paginatedData.meta.last_page;

      // Check if there are more pages
      hasMorePages = paginatedData.meta.current_page < paginatedData.meta.last_page;

      // Check if we've hit the max pages limit
      if (maxPages && currentPage >= startPage + maxPages - 1) {
        hasMorePages = false;
        if (logProgress) {
          logger.debug('[fetchAllPaginatedPages] Reached max pages limit:', maxPages);
        }
      }

      currentPage++;

      // Add delay between requests if specified
      if (delayBetweenPages > 0 && hasMorePages) {
        await new Promise((resolve) => setTimeout(resolve, delayBetweenPages));
      }
    } catch (error) {
      logger.error('[fetchAllPaginatedPages] Error fetching page:', currentPage, error);
      throw error;
    }
  }

  if (logProgress) {
    logger.debug('[fetchAllPaginatedPages] Finished fetching all pages:', {
      totalItems: allItems.length,
      pagesFetched: currentPage - startPage,
      totalPages,
    });
  }

  return allItems;
}

/**
 * Fetch paginated data with callback for each page
 */
export async function fetchPaginatedWithCallback<T>(
  url: string,
  onPageFetched: (items: T[], pageInfo: InterfacePaginatedResponse<T>['meta']) => void | Promise<void>,
  config: PaginationConfig = {},
): Promise<void> {
  const { perPage = 100, startPage = 1, maxPages, delayBetweenPages = 0 } = config;

  let currentPage = startPage;
  let hasMorePages = true;

  while (hasMorePages) {
    try {
      const paginatedData = await fetchPaginatedPage<T>(url, currentPage, perPage);

      // Call the callback with the fetched data
      await onPageFetched(paginatedData.data, paginatedData.meta);

      // Check if there are more pages
      hasMorePages = paginatedData.meta.current_page < paginatedData.meta.last_page;

      // Check if we've hit the max pages limit
      if (maxPages && currentPage >= startPage + maxPages - 1) {
        hasMorePages = false;
      }

      currentPage++;

      // Add delay between requests if specified
      if (delayBetweenPages > 0 && hasMorePages) {
        await new Promise((resolve) => setTimeout(resolve, delayBetweenPages));
      }
    } catch (error) {
      logger.error('[fetchPaginatedWithCallback] Error fetching page:', currentPage, error);
      throw error;
    }
  }
}

/**
 * Create a URL with pagination parameters
 */
export function createPaginatedUrl(baseUrl: string, page: number, perPage: number = 100): string {
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}page=${page}&per_page=${perPage}`;
}

/**
 * Check if a response is paginated
 */
export function isPaginatedResponse(response: unknown): response is InterfacePaginatedResponse<unknown> {
  return (
    response !== null &&
    typeof response === 'object' &&
    'data' in response &&
    'meta' in response &&
    'links' in response &&
    Array.isArray((response as Record<string, unknown>).data) &&
    typeof (response as Record<string, unknown>).meta === 'object' &&
    'current_page' in ((response as Record<string, unknown>).meta as Record<string, unknown>) &&
    'last_page' in ((response as Record<string, unknown>).meta as Record<string, unknown>)
  );
}

/**
 * Extract pagination info from response
 */
export function extractPaginationInfo(response: InterfacePaginatedResponse<unknown>) {
  return {
    currentPage: response.meta.current_page,
    lastPage: response.meta.last_page,
    perPage: response.meta.per_page,
    total: response.meta.total,
    from: response.meta.from,
    to: response.meta.to,
    hasNextPage: response.meta.current_page < response.meta.last_page,
    hasPrevPage: response.meta.current_page > 1,
  };
}
